/**
 * Receiver-side health: what a viewer or guest measures about the media it
 * receives, sent to the host about once per second over a data channel.
 */
import type { ReceiverReport } from './api';
import type { RtcStat } from './stats';

export const HEALTH_CHANNEL_LABEL = 'ohmyobs-health';
export const REPORT_INTERVAL_MS = 1000;
/** A report is a few hundred bytes of JSON; anything much larger is not one. */
export const MAX_REPORT_CHARS = 2048;
/** The host accepts at most one report per this interval from a peer (a few per second). */
export const MIN_REPORT_SPACING_MS = 250;

export type ReportBody = Omit<ReceiverReport, 'receivedAt'>;

/** Cumulative inbound counters of one getStats() call. */
export interface InboundSample {
  atMs: number;
  hasVideo: boolean;
  hasAudio: boolean;
  framesReceived: number;
  framesDecoded: number;
  framesDropped: number;
  frameWidth: number;
  frameHeight: number;
  freezeCount: number;
  totalFreezesDurationSec: number;
  /** Gaps of more than five seconds between frames are counted as pauses, not freezes. */
  pauseCount: number;
  totalPausesDurationSec: number;
  videoPacketsReceived: number;
  videoPacketsLost: number;
  videoJitterBufferDelaySec: number;
  videoJitterBufferEmitted: number;
  videoPlayoutTimestampMs: number | null;
  audioPacketsReceived: number;
  audioPacketsLost: number;
  concealedSamples: number;
  totalSamplesReceived: number;
  totalAudioEnergy: number;
  totalSamplesDurationSec: number;
  audioJitterBufferDelaySec: number;
  audioJitterBufferEmitted: number;
  audioPlayoutTimestampMs: number | null;
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function optional(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function sampleInbound(stats: Iterable<RtcStat>, nowMs: number): InboundSample {
  const sample: InboundSample = {
    atMs: nowMs,
    hasVideo: false,
    hasAudio: false,
    framesReceived: 0,
    framesDecoded: 0,
    framesDropped: 0,
    frameWidth: 0,
    frameHeight: 0,
    freezeCount: 0,
    totalFreezesDurationSec: 0,
    pauseCount: 0,
    totalPausesDurationSec: 0,
    videoPacketsReceived: 0,
    videoPacketsLost: 0,
    videoJitterBufferDelaySec: 0,
    videoJitterBufferEmitted: 0,
    videoPlayoutTimestampMs: null,
    audioPacketsReceived: 0,
    audioPacketsLost: 0,
    concealedSamples: 0,
    totalSamplesReceived: 0,
    totalAudioEnergy: 0,
    totalSamplesDurationSec: 0,
    audioJitterBufferDelaySec: 0,
    audioJitterBufferEmitted: 0,
    audioPlayoutTimestampMs: null,
  };
  for (const stat of stats) {
    if (stat.type !== 'inbound-rtp') continue;
    const kind = typeof stat.kind === 'string' ? stat.kind : stat.mediaType;
    if (kind === 'video') {
      sample.hasVideo = true;
      sample.framesReceived += num(stat.framesReceived);
      sample.framesDecoded += num(stat.framesDecoded);
      sample.framesDropped += num(stat.framesDropped);
      sample.frameWidth = num(stat.frameWidth) || sample.frameWidth;
      sample.frameHeight = num(stat.frameHeight) || sample.frameHeight;
      sample.freezeCount += num(stat.freezeCount);
      sample.totalFreezesDurationSec += num(stat.totalFreezesDuration);
      sample.pauseCount += num(stat.pauseCount);
      sample.totalPausesDurationSec += num(stat.totalPausesDuration);
      sample.videoPacketsReceived += num(stat.packetsReceived);
      sample.videoPacketsLost += Math.max(0, num(stat.packetsLost));
      sample.videoJitterBufferDelaySec += num(stat.jitterBufferDelay);
      sample.videoJitterBufferEmitted += num(stat.jitterBufferEmittedCount);
      sample.videoPlayoutTimestampMs = optional(stat.estimatedPlayoutTimestamp) ?? sample.videoPlayoutTimestampMs;
    } else if (kind === 'audio') {
      sample.hasAudio = true;
      sample.audioPacketsReceived += num(stat.packetsReceived);
      sample.audioPacketsLost += Math.max(0, num(stat.packetsLost));
      sample.concealedSamples += num(stat.concealedSamples);
      sample.totalSamplesReceived += num(stat.totalSamplesReceived);
      sample.totalAudioEnergy += num(stat.totalAudioEnergy);
      sample.totalSamplesDurationSec += num(stat.totalSamplesDuration);
      sample.audioJitterBufferDelaySec += num(stat.jitterBufferDelay);
      sample.audioJitterBufferEmitted += num(stat.jitterBufferEmittedCount);
      sample.audioPlayoutTimestampMs = optional(stat.estimatedPlayoutTimestamp) ?? sample.audioPlayoutTimestampMs;
    }
  }
  return sample;
}

/**
 * What the page's video element actually presented, from requestVideoFrameCallback.
 * `usable` is false while the document is hidden: a hidden page legitimately
 * presents nothing, which must not be reported as a frozen stream.
 */
export interface PresentationSample {
  usable: boolean;
  /**
   * The element plays the audio at zero volume. Chromium measures received audio energy after
   * the element's volume, so in that state "no energy" says nothing about what was received.
   */
  muted: boolean;
  framesPresented: number;
  lastPresentedAtMs: number | null;
}

/** Non-negative difference of two cumulative counters; a reset (new connection) counts from zero. */
function delta(previous: number, current: number): number {
  return current >= previous ? current - previous : current;
}

function jitterBufferMs(prevDelay: number, prevCount: number, delay: number, count: number): number | null {
  const emitted = delta(prevCount, count);
  if (emitted > 0) return (delta(prevDelay, delay) / emitted) * 1000;
  return count > 0 ? (delay / count) * 1000 : null;
}

/** How far each stream's playout timestamp may drift from wall time over one interval. */
export const AV_SYNC_CLOCK_TOLERANCE_MS = 250;
/** Audio concealment above this share of an interval counts as an interruption. */
export const AV_SYNC_MAX_CONCEALED = 0.3;
/** How long the estimate is withheld after an interruption while the playout estimates settle. */
export const AV_SYNC_SETTLE_MS = 10_000;
/** Lip-sync errors beyond this are not a measurement but a broken estimate. */
export const AV_SYNC_MAX_ABS_MS = 3000;

/**
 * The lip-sync estimate is the difference of two `estimatedPlayoutTimestamp`s, each a guess
 * that only holds while its stream plays out steadily. After an audio interruption the audio
 * estimate jumps (measured: values of 22–28 s while the true skew was under 2 ms). It is only
 * reported when both timestamps advanced at the pace of the wall clock, audio really flowed,
 * the last interruption is long enough ago, and the result is plausible.
 */
function avSyncEstimate(
  prev: InboundSample | null,
  cur: InboundSample,
  concealedRatio: number,
): { value: number | null; interrupted: boolean } {
  const audio = cur.audioPlayoutTimestampMs;
  const video = cur.videoPlayoutTimestampMs;
  if (audio === null || video === null) return { value: null, interrupted: false };
  if (!prev || prev.audioPlayoutTimestampMs === null || prev.videoPlayoutTimestampMs === null) {
    return { value: null, interrupted: false };
  }
  const elapsed = cur.atMs - prev.atMs;
  const steady = (from: number, to: number): boolean => Math.abs(to - from - elapsed) <= AV_SYNC_CLOCK_TOLERANCE_MS;
  const audioFlowed = cur.audioPacketsReceived > prev.audioPacketsReceived;
  if (
    elapsed <= 0 ||
    !steady(prev.audioPlayoutTimestampMs, audio) ||
    !steady(prev.videoPlayoutTimestampMs, video) ||
    !audioFlowed ||
    concealedRatio > AV_SYNC_MAX_CONCEALED
  ) {
    return { value: null, interrupted: true };
  }
  // Each value is the capture-clock time of what that stream is playing out now, so the
  // stream with the smaller one is behind. Positive means video is late.
  const value = audio - video;
  return { value: Math.abs(value) > AV_SYNC_MAX_ABS_MS ? null : value, interrupted: false };
}

export interface ReceiverReporter {
  /** Feed one stats sample (and what the element presented); returns the report for the interval since the last one. */
  next(sample: InboundSample, presentation: PresentationSample | null): ReportBody;
}

export function createReceiverReporter(): ReceiverReporter {
  let previous: InboundSample | null = null;
  /** When framesDecoded was last seen to advance; the fallback clock for "last frame age". */
  let lastDecodeAtMs: number | null = null;
  let lastAvInterruptionAtMs: number | null = null;

  return {
    next(sample, presentation) {
      const prev = previous;
      // Every rate is per elapsed time, never per tick: a throttled page calls this late and
      // the interval is simply longer.
      const seconds = prev ? Math.max(0, (sample.atMs - prev.atMs) / 1000) : 0;

      const decoded = prev ? delta(prev.framesDecoded, sample.framesDecoded) : 0;
      if ((prev ? decoded > 0 : sample.framesDecoded > 0) || lastDecodeAtMs === null) lastDecodeAtMs = sample.atMs;

      // Decoded frames are the measure of the stream. What the element presents also depends on
      // the page (hidden tab, covered window), so it is only used to tell more precisely WHEN
      // the last frame appeared, never to declare a stream stalled that is still decoding.
      const fps = seconds > 0 ? decoded / seconds : 0;
      let lastFrameAgeMs = sample.atMs - lastDecodeAtMs;
      const presentedAt = presentation?.usable ? presentation.lastPresentedAtMs : null;
      if (presentedAt !== null && presentedAt !== undefined && prev && presentedAt >= prev.atMs) {
        lastFrameAgeMs = Math.max(0, sample.atMs - presentedAt);
      }

      const concealed = prev ? delta(prev.concealedSamples, sample.concealedSamples) : 0;
      const samples = prev ? delta(prev.totalSamplesReceived, sample.totalSamplesReceived) : 0;
      const energy = prev ? delta(prev.totalAudioEnergy, sample.totalAudioEnergy) : 0;
      const duration = prev ? delta(prev.totalSamplesDurationSec, sample.totalSamplesDurationSec) : 0;

      const lost = prev
        ? delta(prev.videoPacketsLost, sample.videoPacketsLost) + delta(prev.audioPacketsLost, sample.audioPacketsLost)
        : 0;
      const received = prev
        ? delta(prev.videoPacketsReceived, sample.videoPacketsReceived) +
          delta(prev.audioPacketsReceived, sample.audioPacketsReceived)
        : 0;

      const report: ReportBody = {
        framesReceived: sample.framesReceived,
        framesDecoded: sample.framesDecoded,
        framesDropped: sample.framesDropped,
        fps,
        frameWidth: sample.frameWidth,
        frameHeight: sample.frameHeight,
        freezeCount: sample.freezeCount,
        totalFreezesDurationMs: sample.totalFreezesDurationSec * 1000,
        pauseCount: sample.pauseCount,
        totalPausesDurationMs: sample.totalPausesDurationSec * 1000,
        lastFrameAgeMs,
        audioPacketsReceived: sample.audioPacketsReceived,
        audioConcealedRatio: samples > 0 ? Math.min(1, concealed / samples) : 0,
        // RMS level over the interval, as defined for totalAudioEnergy / totalSamplesDuration.
        // Not measurable while the player is muted (see PresentationSample.muted).
        audioLevel: presentation?.muted ? null : duration > 0 ? Math.sqrt(energy / duration) : 0,
        videoJitterBufferMs: sample.hasVideo
          ? jitterBufferMs(
              prev?.videoJitterBufferDelaySec ?? 0,
              prev?.videoJitterBufferEmitted ?? 0,
              sample.videoJitterBufferDelaySec,
              sample.videoJitterBufferEmitted,
            )
          : null,
        audioJitterBufferMs: sample.hasAudio
          ? jitterBufferMs(
              prev?.audioJitterBufferDelaySec ?? 0,
              prev?.audioJitterBufferEmitted ?? 0,
              sample.audioJitterBufferDelaySec,
              sample.audioJitterBufferEmitted,
            )
          : null,
        avSyncMs: null,
        packetLossPct: lost + received > 0 ? (lost / (lost + received)) * 100 : 0,
      };
      const sync = avSyncEstimate(prev, sample, report.audioConcealedRatio);
      if (sync.interrupted) lastAvInterruptionAtMs = sample.atMs;
      const settling = lastAvInterruptionAtMs !== null && sample.atMs - lastAvInterruptionAtMs < AV_SYNC_SETTLE_MS;
      report.avSyncMs = settling ? null : sync.value;
      previous = sample;
      return report;
    },
  };
}

export function encodeReport(report: ReportBody): string {
  return JSON.stringify({ v: 1, ...report });
}

const REQUIRED: ReadonlyArray<[keyof ReportBody, number]> = [
  // [field, upper bound]; every field must be a finite number in 0..bound.
  ['framesReceived', Number.MAX_SAFE_INTEGER],
  ['framesDecoded', Number.MAX_SAFE_INTEGER],
  ['framesDropped', Number.MAX_SAFE_INTEGER],
  ['fps', 1000],
  ['frameWidth', 16_384],
  ['frameHeight', 16_384],
  ['freezeCount', Number.MAX_SAFE_INTEGER],
  ['totalFreezesDurationMs', Number.MAX_SAFE_INTEGER],
  ['pauseCount', Number.MAX_SAFE_INTEGER],
  ['totalPausesDurationMs', Number.MAX_SAFE_INTEGER],
  ['lastFrameAgeMs', Number.MAX_SAFE_INTEGER],
  ['audioPacketsReceived', Number.MAX_SAFE_INTEGER],
  ['audioConcealedRatio', 1],
  ['packetLossPct', 100],
];

function nullableNumber(value: unknown, min: number, max: number): number | null | undefined {
  if (value === null) return null;
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : undefined;
}

/**
 * Validates a report from the wire. The sender is an untrusted browser: anything
 * oversized, malformed or out of range is discarded as a whole (returns null).
 */
export function parseReport(data: unknown, receivedAt: number): ReceiverReport | null {
  if (typeof data !== 'string' || data.length > MAX_REPORT_CHARS) return null;
  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return null;
  }
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return null;
  const o = json as Record<string, unknown>;
  if (o.v !== 1) return null;

  const out: Partial<Record<keyof ReportBody, number | null>> = {};
  for (const [field, max] of REQUIRED) {
    const value = o[field];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > max) return null;
    out[field] = value;
  }
  const videoJitterBufferMs = nullableNumber(o.videoJitterBufferMs, 0, 600_000);
  const audioJitterBufferMs = nullableNumber(o.audioJitterBufferMs, 0, 600_000);
  const avSyncMs = nullableNumber(o.avSyncMs, -600_000, 600_000);
  const audioLevel = nullableNumber(o.audioLevel, 0, 1);
  if (
    videoJitterBufferMs === undefined ||
    audioJitterBufferMs === undefined ||
    avSyncMs === undefined ||
    audioLevel === undefined
  ) {
    return null;
  }
  return {
    ...(out as Omit<ReportBody, 'videoJitterBufferMs' | 'audioJitterBufferMs' | 'avSyncMs' | 'audioLevel'>),
    audioLevel,
    videoJitterBufferMs,
    audioJitterBufferMs,
    avSyncMs,
    receivedAt,
  };
}

export interface PresentationProbe {
  read(): PresentationSample;
  stop(): void;
}

type FrameCallbackVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/** Counts the frames a video element presents. Returns null where the browser lacks the API. */
export function createPresentationProbe(element: HTMLVideoElement): PresentationProbe | null {
  const video = element as FrameCallbackVideo;
  if (typeof video.requestVideoFrameCallback !== 'function') return null;
  let frames = 0;
  let lastAt: number | null = null;
  let handle = 0;
  let stopped = false;
  const onFrame = (): void => {
    if (stopped) return;
    frames += 1;
    lastAt = Date.now();
    handle = video.requestVideoFrameCallback!(onFrame);
  };
  handle = video.requestVideoFrameCallback(onFrame);
  return {
    read: () => ({
      usable: typeof document === 'undefined' || document.visibilityState === 'visible',
      muted: video.muted || video.volume === 0,
      framesPresented: frames,
      lastPresentedAtMs: lastAt,
    }),
    stop() {
      stopped = true;
      video.cancelVideoFrameCallback?.(handle);
    },
  };
}
